import type { CatalogItem } from "@albertine/types/openapi/lmt";
import { useQuery } from "@tanstack/react-query";

export type DistanceResult = {
    driving: string | null;
    walking: string | null;
    isLoading: boolean;
};

export function useDistances(
    item: CatalogItem,
    mapCenter: { lat: number; lng: number } | null,
): DistanceResult {
    const lat = item.locationGeoPoint?.latitude;
    const lng = item.locationGeoPoint?.longitude;
    const enabled =
        typeof google !== "undefined" &&
        typeof lat === "number" &&
        typeof lng === "number" &&
        mapCenter !== null;

    const { data, isLoading } = useQuery({
        queryKey: ["distances", lat, lng, mapCenter?.lat, mapCenter?.lng],
        queryFn: () => {
            const service = new google.maps.DistanceMatrixService();
            return fetchDistances(
                service,
                { lat: lat as number, lng: lng as number },
                mapCenter as { lat: number; lng: number },
            );
        },
        enabled,
    });

    return {
        driving: data?.driving ?? null,
        walking: data?.walking ?? null,
        isLoading,
    };
}

function getDurationText(response: google.maps.DistanceMatrixResponse): string | null {
    const element = response.rows[0]?.elements[0];
    return element?.status === google.maps.DistanceMatrixElementStatus.OK
        ? (element.duration?.text ?? null)
        : null;
}

async function fetchDistances(
    service: google.maps.DistanceMatrixService,
    origin: { lat: number; lng: number },
    destination: { lat: number; lng: number },
) {
    const request = { origins: [origin], destinations: [destination] };
    const [drivingResponse, walkingResponse] = await Promise.all([
        service.getDistanceMatrix({
            ...request,
            travelMode: google.maps.TravelMode.DRIVING,
        }),
        service.getDistanceMatrix({
            ...request,
            travelMode: google.maps.TravelMode.WALKING,
        }),
    ]);
    return {
        driving: getDurationText(drivingResponse),
        walking: getDurationText(walkingResponse),
    };
}
